import { useIntl } from 'react-intl';
import { Button, Divider, Form, Modal } from 'antd';
import { v4 } from 'uuid';
import { Helmet } from 'react-helmet';
import { PageProps } from '@/types/PageProps';
import { DefaultRouteTabs } from '@/components/_common';
import { useHistory } from 'react-router-dom';
import { useEffect, useState } from 'react';

import {
  ConfirmPurchaseFormValues,
  PurchaseImportItemType,
  PurchaseItems,
  PurchaseImportConfirmRequest,
} from '@/core/domain/purchase/purchaseImport/PurchaseImport.types';
import ConfirmImportPurchaseItemsTable from './ConfirmImportPurchaseItemsTable';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useDisclosure } from '@/hooks';
import PurchaseImportConfirmModal from './PurchaseImportConfirmModal';
import { usePurchaseImportFileContext } from '../PurchaseImportFileProvider';
import { usePurchaseImportConfirm } from '@/core/domain/purchase/Purchase.hooks';
import ConfirmImportDescriptions from '@/components/Movements/PurchaseImport/ConfirmImportDescriptions';
import ConfirmImportForm from '@/components/Movements/PurchaseImport/ConfirmImportForm';

function ConfirmImportPurchasePage({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();

  const { purchaseImportData, xml, companyIdSelected } =
    usePurchaseImportFileContext();

  const companyId =
    companyIdSelected || purchaseImportData?.companies?.at(-1)?.id;

  const confirmMutation = usePurchaseImportConfirm(xml);

  const [purchaseItems, setPurchaseItems] = useState<PurchaseItems[]>([]);

  const [form] = Form.useForm<ConfirmPurchaseFormValues>();

  const [modalConfirm, modalConfirmAction] = useDisclosure();

  useEffect(() => {
    if (purchaseImportData) {
      setPurchaseItems(
        purchaseImportData.purchaseItems.map((item, i) => ({
          ...item,
          isAlter: item.partDTO || item.consumableDTO ? false : true,
          linked: item.partDTO || item.consumableDTO ? true : false,
          checked: item.type === PurchaseImportItemType.part,
          index: i,
          key: v4(),
        })),
      );
    }
  }, [purchaseImportData]);

  const handleCancelImport = () => {
    Modal.confirm({
      title: 'Cancelamento da Importação',
      content: 'Realmente deseja cancelar a importação ?',
      onOk: () => history.push('/app/movements/purchases/importPurchase'),
      okType: 'danger',
      okText: 'Sim',
    });
  };

  const handleConfirmImport = async (items: PurchaseItems[]) => {
    if (purchaseImportData && items && companyId) {
      const values = await form.validateFields();

      const obj: PurchaseImportConfirmRequest = {
        ...purchaseImportData,
        companyId: companyId,
        documentTypeId: values.documentTypeId,
        purchaseItems: items.map((item) => {
          const filePurchaseItemFinded =
            purchaseImportData.purchaseItems[item.index].purchaseItemTaxes;

          return {
            ...item,
            type: item.checked
              ? PurchaseImportItemType.part
              : PurchaseImportItemType.consumable,
            purchaseItemTaxes: {
              ...filePurchaseItemFinded,
              cstIpi:
                item.purchaseItemTaxes.cstIpi || filePurchaseItemFinded.cstIpi,
              cstCofins:
                item.purchaseItemTaxes.cstCofins ||
                filePurchaseItemFinded.cstCofins,
              cstPis:
                item.purchaseItemTaxes.cstPis || filePurchaseItemFinded.cstPis,
            },
          };
        }),
      };

      const { purchaseId } = await confirmMutation.mutateAsync(obj);

      history.push(
        `/app/movements/purchases/purchasesReleases/${purchaseId}/general`,
      );
    }
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <DefaultRouteTabs route={route.parent}>
        <ConfirmImportDescriptions />

        <ConfirmImportForm form={form} />

        <Divider />

        <ConfirmImportPurchaseItemsTable
          purchaseItems={purchaseItems}
          setPurchaseItems={setPurchaseItems}
          filePurchaseItems={purchaseImportData?.purchaseItems}
          companyId={companyId}
        />

        <Divider />

        <SpaceForButton>
          <Button
            className="save-button"
            onClick={async () => {
              await form.validateFields();
              modalConfirmAction.open();
            }}
            loading={confirmMutation.isLoading}
            block
          >
            Confirmar Importação
          </Button>

          <Button danger onClick={handleCancelImport} block>
            Cancelar
          </Button>
        </SpaceForButton>

        <PurchaseImportConfirmModal
          open={modalConfirm}
          companyId={companyId}
          onCancel={modalConfirmAction.close}
          items={modalConfirm ? purchaseItems : []}
          onOk={handleConfirmImport}
          confirmLoading={confirmMutation.isLoading}
        />
      </DefaultRouteTabs>
    </>
  );
}

export default ConfirmImportPurchasePage;
